import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';

const MENU_ITEMS = [
  { label: 'Dashboard', path: '/dashboard' },
  { label: 'Client Master', path: '/clients' },
  { label: 'Compliance Calendar', path: '/calendar' },
  { label: 'GST Returns', path: '/gst' },
  { label: 'TDS / TCS', path: '/tds' },
  { label: 'ROC / MCA', path: '/roc' },
  { label: 'Financials', path: '/financials' },
  { label: 'Audit', path: '/audit-status' },
  { label: 'ITR Filing Status', path: '/itr-filing' },
  { label: 'DSC Register', path: '/dsc-register' },
  { label: 'Documents', path: '/legal-docs' },
  { label: 'Data Entry', path: '/data-entry' },
  { label: 'Notes / Reminders', path: '/notes' },
  { label: 'AI Notice', path: '/ai-notice' },
  { label: 'Compliance Setup', path: '/compliance-setup' },
  { label: 'Settings', path: '/settings' },
];

function MenuIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  );
}

function CompanySwitcher() {
  const { companies, selectedCompany, selectedCompanyId, selectCompany } = useCompany();
  const [open, setOpen] = useState(false);

  if (!selectedCompany) return null;

  return (
    <div className="sidebar-company-switcher">
      {open && (
        <div className="sidebar-company-dropdown">
          {companies.map((c) => (
            <button
              key={c.id}
              type="button"
              className={c.id === selectedCompanyId ? 'active' : ''}
              onClick={() => {
                selectCompany(c.id);
                setOpen(false);
              }}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}
      <div className="sidebar-company-current">
        <span className="sidebar-company-name" title={selectedCompany.name}>{selectedCompany.name}</span>
        <button type="button" className="sidebar-switch-btn" onClick={() => setOpen((o) => !o)}>
          Switch
        </button>
      </div>
    </div>
  );
}

function Sidebar({ open = false }) {
  const location = useLocation();
  const { companies, selectedCompany } = useCompany();
  const { user } = useAuth();
  const isBusinessOwner = selectedCompany?.client_mode === 'Business Owner';

  let menuItems = isBusinessOwner ? MENU_ITEMS.filter((item) => item.path !== '/clients') : MENU_ITEMS;
  // Settings is available to owner_admin (platform-wide) and customer_owner
  // (their own tenant - Settings itself narrows the tabs to the ones that
  // are company-scoped). Compliance Setup stays owner_admin-only: custom
  // compliance templates live in a catalog shared across all tenants, so
  // letting one customer add to it would surface their entries to everyone.
  if (user?.role !== 'owner_admin' && user?.role !== 'customer_owner') {
    menuItems = menuItems.filter((item) => item.path !== '/settings');
  }
  if (user?.role !== 'owner_admin') {
    menuItems = menuItems.filter((item) => item.path !== '/compliance-setup');
  }
  // Client role is read-only platform-wide, so the manual-entry forms have nothing for it to do.
  if (user?.role === 'client') {
    menuItems = menuItems.filter((item) => item.path !== '/data-entry');
  }

  // The company switcher was always rendered anchored to the Settings menu
  // item's position. Settings is hidden for non-owner_admin roles, but
  // switching companies isn't a Settings-only concern - so when Settings
  // isn't in the list, fall back to rendering the switcher at the top
  // instead of losing it. owner_admin's layout is unchanged either way.
  const hasSettingsItem = menuItems.some((item) => item.path === '/settings');

  return (
    <aside className={`dash-sidebar${open ? ' open' : ''}`}>
      {!hasSettingsItem && <CompanySwitcher />}
      <nav>
        {menuItems.map((item) => {
          const active = location.pathname === item.path;
          const className = `dash-sidebar-item${active ? ' active' : ''}`;
          const isSettings = item.path === '/settings';

          return (
            <div key={item.label}>
              {isSettings && <CompanySwitcher />}
              <Link to={item.path} className={className}>
                <MenuIcon />
                <span>{item.label}</span>
                {isSettings && companies.length > 0 && (
                  <span className="sidebar-badge">{companies.length}</span>
                )}
              </Link>
            </div>
          );
        })}
      </nav>
    </aside>
  );
}

export default Sidebar;
